import React from 'react';

import { Box, Grid, Stack } from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useField } from 'formik';
import { isEqual } from 'lodash';

import TimePicker from '../../../../../components/Formiks/CustomTimePicker/CustomTimePicker';
import DatePicker, {
  SlotProps as DateSlotProps,
} from '../../../../../components/Formiks/DatePicker/DatePicker';
import FormikTextField from '../../../../../components/Formiks/TextField/TextField';
import GooglePlaceAutoComplete from '../../../../../components/GooglePlaceAutoComplete/GooglePlaceAutoComplete';
import { AddressDetail } from '../../../../../components/GooglePlaceAutoComplete/types';
import { OfferCategory } from '../../../../../graphql/API';
import { useAppSelector } from '../../../../../redux/hooks';
import { Method } from '../../../OfferForm';
import { AttentionError } from '../../AttentionError';
import { AvailableDays } from '../../AvailableDays';
import { AvailableSpots } from '../../AvailableSpots';
import { OfferRepeat } from '../../OfferRepeat';
import { FormValue, StepOneFormValue, StepTwoFormValue } from '../../schema';
import { getEventTimeZoneFromCoordinates } from '../../../../../utils/dateV2';
import config from '../../../../../config';

type Values = StepOneFormValue & StepTwoFormValue;

type Props = {
  method?: Method;
  initialValues?: Values;
  values: Values;
  handleAddress: (address: AddressDetail | null) => void;
  addressFieldError?: boolean;
  addressFieldHelperText?: string;
  handlePersistFormData?: (values: Partial<FormValue>) => void;
};

const slotProps: DateSlotProps = {
  textField: {
    size: 'small',
    fullWidth: true,
  },
};

export const Form2: React.FC<Props> = ({
  initialValues,
  values,
  method = 'insert',
  handleAddress,
  addressFieldError,
  addressFieldHelperText,
  handlePersistFormData,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const [waitTimeField, , waitTimeHelper] = useField('waitTime');
  const [offerTimeZoneIdField, , offerTimeZoneIdHelper] = useField('offerTimeZoneId');
  const [availableDaysField] = useField('availableDays');
  const [, , endDateFieldHelper] = useField('endDate');

  React.useEffect(() => {
    if (waitTimeField.value == 2 && availableDaysField.value.length === 2) {
      waitTimeHelper.setValue(0);
    }
  }, [availableDaysField.value, waitTimeField.value, waitTimeHelper]);

  React.useEffect(() => {
    if (handlePersistFormData) handlePersistFormData({ ...(values as FormValue) });
  }, [values]);

  React.useEffect(() => {
    const { startDate, startTime, endTime } = values;

    if (startDate && startTime && endTime) {
      const fromDate = dayjs.utc(startDate);
      const toDate = dayjs.utc(startDate);

      const startTimeStr = dayjs(startTime).format('HH:mm');
      const endTimeStr = dayjs(endTime).format('HH:mm');

      const start = dayjs(`${fromDate.format('YYYY-MM-DD')} ${startTimeStr}`);
      const end = dayjs(`${toDate.format('YYYY-MM-DD')} ${endTimeStr}`);

      if (end.isBefore(start)) {
        const newEndDate = fromDate.add(1, 'day').toDate();
        endDateFieldHelper.setValue(newEndDate);
      } else {
        endDateFieldHelper.setValue(fromDate.toDate());
      }
    }
  }, [values.startTime, values.endTime, values.startDate]);

  React.useEffect(() => {
    if (values.location && config.googleMapApiKey) {
      getOfferTimeZoneIdFromEventLocation(values.location?.lat ?? 0, values.location?.lon ?? 0, config.googleMapApiKey);
    }
  }, [values.location, config.googleMapApiKey]);

  const getOfferTimeZoneIdFromEventLocation = async (lat: number, lon: number, googleMapsApiKey: string) => {
    try {
      if(lat && lon && googleMapsApiKey) {
        const timeZone = await getEventTimeZoneFromCoordinates(lat, lon, googleMapsApiKey);
        if (timeZone && timeZone.timeZoneId) {
          offerTimeZoneIdHelper.setValue(timeZone.timeZoneId);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const isStartDateToday = React.useMemo(() => {
    if (!values.startDate || values.category !== OfferCategory.event) return false;
    const today = dayjs().startOf('day');
    const startDate = dayjs(values.startDate).startOf('day');
    return today.isSame(startDate, 'day');
  }, [values.startDate, values.category]);

  return (
    <Stack spacing={6} width={'100%'}>
      {values.category === OfferCategory.event && (
        <CustomBox>
          <GooglePlaceAutoComplete
            type='offer'
            onChange={handleAddress}
            error={addressFieldError}
            helperText={addressFieldHelperText || ''}
            placeAddress={venueState?.address1 || ''}
          />
        </CustomBox>
      )}

      {values.address && (
        <CustomBox>
          <label htmlFor='txt-address' className='offer-form-label'>
            Address
          </label>
          <FormikTextField
            id='txt-address'
            name='address'
            placeholder='Ex. address'
            disabled
            className='offer-input-field'
            value={values.address}
          />
        </CustomBox>
      )}

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={values.category === OfferCategory.event ? 8 : 12}>
            <CustomBox>
              <label htmlFor='' className='offer-form-label'>
                Start date
              </label>
              <DatePicker
                minDate={dayjs()}
                format='D MMMM YYYY'
                name='startDate'
                slotProps={slotProps as DateSlotProps}
                className='offer-input-field'
              />
              {initialValues &&
                method === 'update' &&
                !isEqual(initialValues.startDate, values.startDate) && <AttentionError />}
            </CustomBox>
          </Grid>
          {values.category === OfferCategory.event && (
            <Grid item xs={4}>
              <CustomBox>
                <label htmlFor='timeFrom' className='offer-form-label'>
                  Time from
                </label>
                <TimePicker
                  name='startTime'
                  className='offer-time-input-field'
                  sx={{ maxWidth: '100%' }}
                  id='timeFrom'
                  shouldDisaledThePreviousTime={isStartDateToday}
                />
                {initialValues &&
                  method === 'update' &&
                  initialValues.startTime !== values.startTime && <AttentionError />}
              </CustomBox>
            </Grid>
          )}
        </Grid>
      </Box>

      {values.category !== OfferCategory.event && (
        <AvailableDays initialValues={initialValues} method={method} />
      )}

      <Box>
        <Grid container spacing={2}>
          {values.category === OfferCategory.event ? (
            <Grid item xs={8}>
              <CustomBox>
                <label htmlFor='' className='offer-form-label'>
                  End date
                </label>
                <DatePicker
                  minDate={dayjs()}
                  format='D MMMM YYYY'
                  name='endDate'
                  slotProps={slotProps as DateSlotProps}
                  className='offer-input-field'
                  disabled
                />
                {initialValues &&
                  method === 'update' &&
                  !isEqual(initialValues.startDate, values.startDate) && <AttentionError />}
              </CustomBox>
            </Grid>
          ) : (
            <Grid item xs={6}>
              <CustomBox>
                <label htmlFor='timeFrom' className='offer-form-label'>
                  Time from
                </label>
                <TimePicker
                  name='startTime'
                  className='offer-time-input-field'
                  sx={{ maxWidth: '100%' }}
                  id='timeFrom'
                  shouldDisaledThePreviousTime={isStartDateToday}
                />
                {initialValues &&
                  method === 'update' &&
                  initialValues.startTime !== values.startTime && <AttentionError />}
              </CustomBox>
            </Grid>
          )}
          <Grid item xs={values.category === OfferCategory.event ? 4 : 6}>
            <CustomBox>
              <label htmlFor='endTime' className='offer-form-label'>
                Time to
              </label>
              <TimePicker
                name='endTime'
                className='offer-time-input-field'
                sx={{ maxWidth: '100%' }}
                id='endTime'
                shouldDisaledThePreviousTime={isStartDateToday}
              />
              {initialValues && method === 'update' && initialValues.endTime !== values.endTime && (
                <AttentionError />
              )}
            </CustomBox>
          </Grid>
        </Grid>
      </Box>

      <Box>
        <AvailableSpots />
        {initialValues && method === 'update' && initialValues.spots != values.spots && (
          <AttentionError />
        )}
      </Box>
      {values.category !== OfferCategory.event && (
        <Box>
          <OfferRepeat values={values} />
          {initialValues && method === 'update' && initialValues.waitTime !== values.waitTime && (
            <AttentionError />
          )}
        </Box>
      )}
    </Stack>
  );
};

const CustomBox = styled.div`
  display: flex;
  flex-direction: column;
  fieldset {
    border: 1px solid #e6e6e6;
  }
`;
